
import { useEffect, useMemo, useState } from "react";
import { apiRequest } from "../api";
import "./css/AdditionalReportsView.css";

const MAX_IDENTIFIERS = 6;

const EMPTY_IDENTIFIER = {
  secondaryHeader: "",
  primaryHeader: "",
};

const createEmptyIdentifiers = () =>
  Array.from(
    { length: MAX_IDENTIFIERS },
    () => ({ ...EMPTY_IDENTIFIER })
  );

const getIdentifierKey = (index) => `identifier${index + 1}`;

export default function AdditionalReportsView() {
  const [activeTab, setActiveTab] = useState("add");

  // ---------------------------------------------------------
  // Configured reports
  // ---------------------------------------------------------

  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // ---------------------------------------------------------
  // Form
  // ---------------------------------------------------------

  const [reportName, setReportName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  const [headers, setHeaders] = useState([]);

  const [identifiers, setIdentifiers] = useState(
    createEmptyIdentifiers()
  );

  const [connectingReport, setConnectingReport] = useState("");

  // ---------------------------------------------------------
  // UI state
  // ---------------------------------------------------------

  const [editingReportId, setEditingReportId] = useState(null);

  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingFileHeaders, setLoadingFileHeaders] = useState(false);
  const [savingReport, setSavingReport] = useState(false);

  const [detailsReport, setDetailsReport] = useState(null);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");


  // ---------------------------------------------------------
  // Primary report headers
  // ---------------------------------------------------------

  const [primaryHeaders, setPrimaryHeaders] = useState({
    PO: [],
    GRN: [],
    VendorMasters: [],
  });

  
  const [uploadReportId, setUploadReportId] = useState("");
  const [uploadFile, setUploadFile] = useState(null);

  const [uploadRows, setUploadRows] = useState([]);
  const [uploadHeaders, setUploadHeaders] = useState([]);

  const [uploadPage, setUploadPage] = useState(1);
  const [uploadPageSize] = useState(25);
  const [uploadTotalCount, setUploadTotalCount] = useState(0);
  const [uploadTotalPages, setUploadTotalPages] = useState(0);

  const [loadingUploadData, setLoadingUploadData] = useState(false);
  const [uploadingReport, setUploadingReport] = useState(false);



  // ---------------------------------------------------------
  // Load primary report headers
  // ---------------------------------------------------------

  useEffect(() => {
    loadPrimaryHeaders();
    loadReports();
  }, []);

  async function loadPrimaryHeaders() {
    try {
      const response = await apiRequest(
        "/primary-reports/headers/",
        {
          method: "GET",
        }
      );

      const data = response?.data || {};

      setPrimaryHeaders({
        PO: Array.isArray(data.PO) ? data.PO : [],
        GRN: Array.isArray(data.GRN) ? data.GRN : [],
        VendorMasters: Array.isArray(data.VendorMasters)
          ? data.VendorMasters
          : [],
      });
    } catch (err) {
      setError(
        err?.message ||
          "Unable to load primary report headers."
      );
    }
  }

  // ---------------------------------------------------------
  // Load configured reports
  // ---------------------------------------------------------

  async function loadReports() {
    setLoadingReports(true);

    try {
      const response = await apiRequest(
        "/secondary-reports/",
        {
          method: "GET",
        }
      );

      const reportList = response?.data?.reports;

      setReports(
        Array.isArray(reportList)
          ? reportList
          : []
      );
    } catch (err) {
      setError(
        err?.message ||
          "Unable to load configured reports."
      );
    } finally {
      setLoadingReports(false);
    }
  }

  // ---------------------------------------------------------
  // Reset form
  // ---------------------------------------------------------

  function resetForm() {
    setReportName("");
    setSelectedFile(null);
    setHeaders([]);
    setIdentifiers(createEmptyIdentifiers());
    setConnectingReport("");
    setEditingReportId(null);

    // Reset actual file input.
    const fileInput = document.getElementById(
      "additional-report-file"
    );

    if (fileInput) {
      fileInput.value = "";
    }
  }

  // ---------------------------------------------------------
  // Add mode
  // ---------------------------------------------------------

  function startNewReport() {
    setError("");
    setMessage("");

    resetForm();
    setActiveTab("add");
  }

  // ---------------------------------------------------------
  // File selection
  // ---------------------------------------------------------

  async function handleFileChange(event) {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setError("");
    setMessage("");
    setSelectedFile(file);
    setLoadingFileHeaders(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiRequest(
        "/secondary-reports/headers/",
        {
          method: "POST",
          body: formData,
        }
      );

      const newHeaders =
        response?.headers ||
        response?.data?.headers ||
        [];

      if (!Array.isArray(newHeaders)) {
        throw new Error(
          "Backend did not return valid report headers."
        );
      }

      if (!newHeaders.length) {
        throw new Error(
          "No headers were found in the selected XLSX file."
        );
      }

      // -----------------------------------------------------
      // IMPORTANT:
      // New XLSX replaces the current headers.
      // Preserve identifiers whose secondary header still
      // exists. Clear only invalid mappings.
      // -----------------------------------------------------

      setHeaders(newHeaders);

      setIdentifiers((previous) =>
        previous.map((item) => {
          if (
            item.secondaryHeader &&
            newHeaders.includes(item.secondaryHeader)
          ) {
            return item;
          }

          return {
            secondaryHeader: "",
            primaryHeader: "",
          };
        })
      );

      setMessage(
        `XLSX loaded successfully. ${newHeaders.length} headers found.`
      );
    } catch (err) {
      setSelectedFile(null);

      setError(
        err?.message ||
          "Unable to process the selected XLSX file."
      );
    } finally {
      setLoadingFileHeaders(false);
    }
  }

  // ---------------------------------------------------------
  // Identifier update
  // ---------------------------------------------------------

  function updateIdentifier(index, field, value) {
    setIdentifiers((previous) =>
      previous.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        const updated = {
          ...item,
          [field]: value,
        };

        // If the secondary header changes, keep the primary
        // mapping for now. The user can change it separately.
        return updated;
      })
    );
  }

  // ---------------------------------------------------------
  // Prevent duplicate secondary headers
  // ---------------------------------------------------------

  const selectedSecondaryHeaders = useMemo(() => {
    return identifiers
      .map((item) => item.secondaryHeader)
      .filter(Boolean);
  }, [identifiers]);

  function isSecondaryHeaderAlreadySelected(
    header,
    currentIndex
  ) {
    return identifiers.some(
      (item, index) =>
        index !== currentIndex &&
        item.secondaryHeader === header
    );
  }

  // ---------------------------------------------------------
  // Available primary headers
  // ---------------------------------------------------------

  const availablePrimaryHeaders =
    connectingReport
      ? primaryHeaders[connectingReport] || []
      : [];

  // ---------------------------------------------------------
  // Primary report change
  // ---------------------------------------------------------

  function handleConnectingReportChange(value) {
    setConnectingReport(value);

    // Primary mappings belong to the selected primary report.
    // Therefore clear them when the primary report changes.
    setIdentifiers((previous) =>
      previous.map((item) => ({
        ...item,
        primaryHeader: "",
      }))
    );
  }

  // ---------------------------------------------------------
  // Validation
  // ---------------------------------------------------------

  function validateForm() {
    if (!reportName.trim()) {
      return "Report name is required.";
    }

    if (!headers.length) {
      return "Please select an XLSX file first.";
    }

    if (!connectingReport) {
      return "Please select a primary report.";
    }

    const configuredIdentifiers = identifiers.filter(
      (item) => item.secondaryHeader
    );

    if (!configuredIdentifiers.length) {
      return "Please configure at least one identifier.";
    }

    for (let index = 0; index < identifiers.length; index++) {
      const item = identifiers[index];

      if (!item.secondaryHeader) {
        if (item.primaryHeader) {
          return `Please select a secondary header for Identifier ${
            index + 1
          }.`;
        }

        continue;
      }

      if (!item.primaryHeader) {
        return `Please select a connecting column for Identifier ${
          index + 1
        }.`;
      }

      if (
        isSecondaryHeaderAlreadySelected(
          item.secondaryHeader,
          index
        )
      ) {
        return `The header "${item.secondaryHeader}" is already selected for another identifier.`;
      }
    }

    return null;
  }

  //   ---------------------------------------------------------
  //   load data when report chnages
  // ---------------------------------------------------------

  const loadUploadReportData = async (reportId, page = 1) => {
    if (!reportId) {
        setUploadRows([]);
        setUploadHeaders([]);
        setUploadTotalCount(0);
        setUploadTotalPages(0);
        return;
    }

    try {
        setLoadingUploadData(true);

        const response = await apiRequest(
        `/secondary-reports/data/${reportId}/?page=${page}&page_size=${uploadPageSize}`,
        {
            method: "GET",
        }
        );

        const data = response?.data || {};

        setUploadHeaders(data.headers || []);
        setUploadRows(data.rows || []);
        setUploadTotalCount(data.count || 0);
        setUploadTotalPages(data.total_pages || 0);
        setUploadPage(data.page || page);
    } catch (error) {
        setError(
        error?.message || "Unable to load report data."
        );

        setUploadRows([]);
        setUploadHeaders([]);
    } finally {
        setLoadingUploadData(false);
    }
    };

  const handleUploadReportChange = async (event) => {
    const reportId = event.target.value;

    setUploadReportId(reportId);
    setUploadFile(null);
    setUploadPage(1);

    await loadUploadReportData(reportId, 1);
    };

  const handleUploadReport = async () => {
    if (!uploadReportId) {
        setError("Please select a report.");
        return;
    }

    if (!uploadFile) {
        setError("Please select an XLSX file.");
        return;
    }

    try {
        setUploadingReport(true);
        setError("");
        setSuccess("");

        const formData = new FormData();
        formData.append("file", uploadFile);

        const response = await apiRequest(
        `/secondary-reports/data/${uploadReportId}/upload/`,
        {
            method: "POST",
            body: formData,
        }
        );

        setSuccess(
        response?.message ||
        "Report uploaded successfully. Previous data was replaced."
        );

        setUploadFile(null);
        setUploadPage(1);

        await loadUploadReportData(uploadReportId, 1);
    } catch (error) {
        const payload = error?.payload || {};

        if (Array.isArray(payload.missing_columns)) {
        setError(
            `Missing required columns: ${payload.missing_columns.join(", ")}`
        );
        } else {
        setError(
            error?.message || "Unable to upload report."
        );
        }
    } finally {
        setUploadingReport(false);
    }
    };

  const renderUploadReportTable = () => {
    if (loadingUploadData) {
        return (
        <div className="additional-reports-upload-loading">
            Loading report data...
        </div>
        );
    }

    if (!uploadReportId) {
        return (
        <div className="additional-reports-upload-empty">
            Select a report to view its existing data.
        </div>
        );
    }

    if (!uploadRows.length) {
        return (
        <div className="additional-reports-upload-empty">
            No data uploaded for this report yet.
        </div>
        );
    }

    return (
        <div className="additional-reports-upload-table-wrap">
        <table className="additional-reports-upload-table">
            <thead>
            <tr>
                {uploadHeaders.map((header) => (
                <th key={header}>
                    {header}
                </th>
                ))}
            </tr>
            </thead>

            <tbody>
            {uploadRows.map((row) => {
                const rawData = row.raw_data || {};

                return (
                <tr key={row.id}>
                    {uploadHeaders.map((header) => (
                    <td key={`${row.id}-${header}`}>
                        {rawData[header] === null ||
                        rawData[header] === undefined
                        ? ""
                        : String(rawData[header])}
                    </td>
                    ))}
                </tr>
                );
            })}
            </tbody>
        </table>
        </div>
    );
    };

  const handleUploadPageChange = async (page) => {
    if (
        page < 1 ||
        page > uploadTotalPages ||
        page === uploadPage
    ) {
        return;
    }

    await loadUploadReportData(
        uploadReportId,
        page
    );
    };

  const renderUploadPagination = () => {
    if (!uploadReportId || uploadTotalPages <= 1) {
        return null;
    }

    return (
        <div className="additional-reports-upload-pagination">
        <button
            type="button"
            disabled={uploadPage <= 1 || loadingUploadData}
            onClick={() =>
            handleUploadPageChange(uploadPage - 1)
            }
        >
            ←
        </button>

        <span>
            Page {uploadPage} of {uploadTotalPages}
        </span>

        <button
            type="button"
            disabled={
            uploadPage >= uploadTotalPages ||
            loadingUploadData
            }
            onClick={() =>
            handleUploadPageChange(uploadPage + 1)
            }
        >
            →
        </button>
        </div>
    );
    };

  // ---------------------------------------------------------
  // Build API payload
  // ---------------------------------------------------------

  function buildPayload() {
    const payloadIdentifiers = {};
    const payloadConnectingColumns = {};

    identifiers.forEach((item, index) => {
      if (!item.secondaryHeader) {
        return;
      }

      const key = getIdentifierKey(index);

      payloadIdentifiers[key] =
        item.secondaryHeader;

      payloadConnectingColumns[key] =
        item.primaryHeader;
    });

    return {
      report_name: reportName.trim(),
      headers,
      identifiers: payloadIdentifiers,
      connecting_report: connectingReport,
      connecting_columns: payloadConnectingColumns,
    };
  }

  // ---------------------------------------------------------
  // Save / Update
  // ---------------------------------------------------------

  async function handleSubmit(event) {
    event.preventDefault();

    setError("");
    setMessage("");

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = buildPayload();

    setSavingReport(true);

    try {
      let response;

      if (editingReportId) {
        response = await apiRequest(
          `/secondary-reports/${editingReportId}/update/`,
          {
            method: "PUT",
            body: payload,
          }
        );
      } else {
        response = await apiRequest(
          "/secondary-reports/create/",
          {
            method: "POST",
            body: payload,
          }
        );
      }

      setMessage(
        response?.message ||
          (
            editingReportId
              ? "Additional report updated successfully."
              : "Additional report saved successfully."
          )
      );

      resetForm();

      await loadReports();

      setActiveTab("add");
    } catch (err) {
      // apiRequest already puts backend payload on error.
      const backendMessage =
        err?.payload?.message ||
        err?.message;

      setError(
        backendMessage ||
          (
            editingReportId
              ? "Unable to update the report."
              : "Unable to save the report."
          )
      );
    } finally {
      setSavingReport(false);
    }
  }

  // ---------------------------------------------------------
  // Details
  // ---------------------------------------------------------

  async function handleDetails(reportId) {
    setError("");
    setMessage("");
    setLoadingDetails(true);

    try {
      const response = await apiRequest(
        `/secondary-reports/${reportId}/`,
        {
          method: "GET",
        }
      );

      setDetailsReport(
        response?.data || null
      );
    } catch (err) {
      setError(
        err?.message ||
          "Unable to load report details."
      );
    } finally {
      setLoadingDetails(false);
    }
  }

  // ---------------------------------------------------------
  // Edit
  // ---------------------------------------------------------

  async function handleEdit(reportId) {
    setError("");
    setMessage("");
    setLoadingDetails(true);

    try {
      const response = await apiRequest(
        `/secondary-reports/${reportId}/`,
        {
          method: "GET",
        }
      );

      const report = response?.data;

      if (!report) {
        throw new Error(
          "Report details were not returned by the backend."
        );
      }

      setEditingReportId(report.id);
      setReportName(report.report_name || "");

      // IMPORTANT:
      // Existing stored headers are used.
      // No XLSX is required during edit.
      setHeaders(
        Array.isArray(report.headers)
          ? report.headers
          : []
      );

      setConnectingReport(
        report.connecting_report || ""
      );

      const storedIdentifiers =
        report.identifiers || {};

      const storedConnectingColumns =
        report.connecting_columns || {};

      const populatedIdentifiers =
        createEmptyIdentifiers();

      for (
        let index = 0;
        index < MAX_IDENTIFIERS;
        index++
      ) {
        const key = getIdentifierKey(index);

        populatedIdentifiers[index] = {
          secondaryHeader:
            storedIdentifiers[key] || "",

          primaryHeader:
            storedConnectingColumns[key] || "",
        };
      }

      setIdentifiers(
        populatedIdentifiers
      );

      setSelectedFile(null);

      const fileInput =
        document.getElementById(
          "additional-report-file"
        );

      if (fileInput) {
        fileInput.value = "";
      }

      setActiveTab("add");

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (err) {
      setError(
        err?.message ||
          "Unable to load report for editing."
      );
    } finally {
      setLoadingDetails(false);
    }
  }

  // ---------------------------------------------------------
  // Delete
  // ---------------------------------------------------------

  function handleDelete(report) {
    // Intentionally not implemented yet.
    // Delete will have additional backend functionality
    // that needs to be defined first.

    setError(
      `Delete functionality for "${report.report_name}" will be implemented after the deletion workflow is defined.`
    );
  }

  // ---------------------------------------------------------
  // Details modal
  // ---------------------------------------------------------

  function closeDetails() {
    setDetailsReport(null);
  }

  // ---------------------------------------------------------
  // Render
  // ---------------------------------------------------------

  return (
    <section className="additional-reports-view">

      {/* =====================================================
          Header
      ====================================================== */}

      <div className="additional-reports-header">
        <div>
          <h1>Additional Reports</h1>

          <p>
            Configure secondary reports and map their
            identifiers to your primary reports.
          </p>
        </div>

        <button
          type="button"
          className="additional-reports-add-button"
          onClick={startNewReport}
        >
          + Add Report
        </button>
      </div>

      {/* =====================================================
          Messages
      ====================================================== */}

      {message ? (
        <div className="additional-reports-message success">
          {message}
        </div>
      ) : null}

      {error ? (
        <div className="additional-reports-message error">
          {error}
        </div>
      ) : null}

      {/* =====================================================
          Tabs
      ====================================================== */}

      <div className="additional-reports-tabs">
        <button
          type="button"
          className={
            activeTab === "add"
              ? "additional-reports-tab active"
              : "additional-reports-tab"
          }
          onClick={() => setActiveTab("add")}
        >
          Add Report
        </button>

        <button
          type="button"
          className={
            activeTab === "upload"
              ? "additional-reports-tab active"
              : "additional-reports-tab"
          }
          onClick={() => setActiveTab("upload")}
        >
          Upload Report
        </button>
      </div>

      {/* =====================================================
          Add / Edit Tab
      ====================================================== */}

      {activeTab === "add" ? (
        <div className="additional-reports-content">

          {/* =================================================
              Form
          ================================================== */}

          <form
            className="additional-reports-form"
            onSubmit={handleSubmit}
          >

            <div className="additional-reports-form-header">
              <div>
                <h2>
                  {editingReportId
                    ? "Edit Report"
                    : "Add Report"}
                </h2>

                {editingReportId ? (
                  <p>
                    Existing report headers are being used.
                    Selecting a new XLSX is optional.
                  </p>
                ) : (
                  <p>
                    Select an XLSX file to load its headers.
                  </p>
                )}
              </div>
            </div>

            {/* =================================================
                Report Name
            ================================================== */}

            <div className="additional-reports-field">
              <label htmlFor="additional-report-name">
                Report Name
              </label>

              <input
                id="additional-report-name"
                type="text"
                value={reportName}
                onChange={(event) =>
                  setReportName(event.target.value)
                }
                placeholder="Enter report name"
                disabled={savingReport}
              />
            </div>

            {/* =================================================
                XLSX
            ================================================== */}

            <div className="additional-reports-field">
              <label htmlFor="additional-report-file">
                XLSX File
                {editingReportId ? " (Optional)" : ""}
              </label>

              <input
                id="additional-report-file"
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                disabled={
                  savingReport ||
                  loadingFileHeaders
                }
              />

              {editingReportId &&
              !selectedFile ? (
                <small className="additional-reports-help">
                  No new file selected. Existing stored
                  headers will be used.
                </small>
              ) : null}

              {selectedFile ? (
                <small className="additional-reports-help">
                  Selected: {selectedFile.name}
                </small>
              ) : null}

              {loadingFileHeaders ? (
                <small className="additional-reports-help">
                  Reading XLSX headers...
                </small>
              ) : null}
            </div>

            {/* =================================================
                Loaded Headers
            ================================================== */}

            {headers.length ? (
              <div className="additional-reports-header-info">
                <strong>
                  Available Headers ({headers.length})
                </strong>

                <div className="additional-reports-header-list">
                  {headers.map((header, index) => (
                    <span
                      key={`${header}-${index}`}
                      className="additional-reports-header-chip"
                    >
                      {header}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {/* =================================================
                Primary Report
            ================================================== */}

            <div className="additional-reports-field">
              <label htmlFor="additional-report-primary">
                Primary Report
              </label>

              <select
                id="additional-report-primary"
                value={connectingReport}
                onChange={(event) =>
                  handleConnectingReportChange(
                    event.target.value
                  )
                }
                disabled={
                  savingReport ||
                  !headers.length
                }
              >
                <option value="">
                  Select Primary Report
                </option>

                <option value="PO">
                  PO
                </option>

                <option value="GRN">
                  GRN
                </option>

                <option value="VendorMasters">
                  Vendor Masters
                </option>
              </select>
            </div>

            {/* =================================================
                Identifiers
            ================================================== */}

            <div className="additional-reports-identifiers">

              <div className="additional-reports-section-title">
                <h3>Identifiers</h3>

                <p>
                  Select up to 6 unique secondary report
                  headers and map each one to a primary
                  report column.
                </p>
              </div>

              {identifiers.map((item, index) => {
                const identifierNumber =
                  index + 1;

                const identifierKey =
                  getIdentifierKey(index);

                return (
                  <div
                    className="additional-reports-identifier-row"
                    key={identifierKey}
                  >

                    <div className="additional-reports-identifier-number">
                      Identifier {identifierNumber}
                    </div>

                    {/* -----------------------------------------
                        Secondary header
                    ------------------------------------------ */}

                    <div className="additional-reports-field">
                      <label>
                        Secondary Header
                      </label>

                      <select
                        value={
                          item.secondaryHeader
                        }
                        onChange={(event) =>
                          updateIdentifier(
                            index,
                            "secondaryHeader",
                            event.target.value
                          )
                        }
                        disabled={
                          savingReport ||
                          !headers.length
                        }
                      >
                        <option value="">
                          Select Header
                        </option>

                        {headers.map(
                          (header, headerIndex) => {
                            const alreadySelected =
                              isSecondaryHeaderAlreadySelected(
                                header,
                                index
                              );

                            return (
                              <option
                                key={`${header}-${headerIndex}`}
                                value={header}
                                disabled={
                                  alreadySelected
                                }
                              >
                                {header}
                              </option>
                            );
                          }
                        )}
                      </select>
                    </div>

                    {/* -----------------------------------------
                        Primary connecting column
                    ------------------------------------------ */}

                    <div className="additional-reports-field">
                      <label>
                        Connecting Column
                      </label>

                      <select
                        value={
                          item.primaryHeader
                        }
                        onChange={(event) =>
                          updateIdentifier(
                            index,
                            "primaryHeader",
                            event.target.value
                          )
                        }
                        disabled={
                          savingReport ||
                          !connectingReport ||
                          !item.secondaryHeader
                        }
                      >
                        <option value="">
                          Select Connecting Column
                        </option>

                        {availablePrimaryHeaders.map(
                          (
                            header,
                            headerIndex
                          ) => (
                            <option
                              key={`${header}-${headerIndex}`}
                              value={header}
                            >
                              {header}
                            </option>
                          )
                        )}
                      </select>
                    </div>

                  </div>
                );
              })}
            </div>

            {/* =================================================
                Submit
            ================================================== */}

            <div className="additional-reports-form-actions">

              {editingReportId ? (
                <button
                  type="button"
                  className="additional-reports-secondary-button"
                  onClick={startNewReport}
                  disabled={savingReport}
                >
                  Cancel Edit
                </button>
              ) : null}

              <button
                type="submit"
                className="additional-reports-primary-button"
                disabled={
                  savingReport ||
                  loadingFileHeaders
                }
              >
                {savingReport
                  ? (
                    editingReportId
                      ? "Updating..."
                      : "Saving..."
                  )
                  : (
                    editingReportId
                      ? "Update Report"
                      : "Save Report"
                  )}
              </button>
            </div>

          </form>

          {/* =================================================
              Configured Reports
          ================================================== */}

          <div className="additional-reports-list-section">

            <div className="additional-reports-list-header">
              <div>
                <h2>
                  Configured Reports
                </h2>

                <p>
                  {loadingReports
                    ? "Loading reports..."
                    : `${reports.length} report${
                        reports.length === 1
                          ? ""
                          : "s"
                      } configured`}
                </p>
              </div>

              <button
                type="button"
                className="additional-reports-refresh-button"
                onClick={loadReports}
                disabled={loadingReports}
              >
                Refresh
              </button>
            </div>

            {loadingReports ? (
              <div className="additional-reports-empty">
                Loading configured reports...
              </div>
            ) : reports.length === 0 ? (
              <div className="additional-reports-empty">
                No additional reports have been configured yet.
              </div>
            ) : (
              <div className="additional-reports-list">

                {reports.map((report) => (
                  <div
                    className="additional-reports-card"
                    key={report.id}
                  >

                    <div className="additional-reports-card-info">

                      <h3>
                        {report.report_name}
                      </h3>

                      <div className="additional-reports-card-meta">
                        <span>
                          {report.identifier_count}{" "}
                          identifier
                          {report.identifier_count === 1
                            ? ""
                            : "s"}
                        </span>

                        <span>
                          Primary:{" "}
                          {report.connecting_report}
                        </span>
                      </div>

                    </div>

                    <div className="additional-reports-card-actions">

                      <button
                        type="button"
                        onClick={() =>
                          handleDetails(report.id)
                        }
                        disabled={loadingDetails}
                      >
                        Details
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          handleEdit(report.id)
                        }
                        disabled={loadingDetails}
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        className="danger"
                        onClick={() =>
                          handleDelete(report)
                        }
                      >
                        Delete
                      </button>

                    </div>

                  </div>
                ))}

              </div>
            )}

          </div>

        </div>
      ) : null}

      {/* =====================================================
          Upload Tab
      ====================================================== */}

      {activeTab === "upload" && (
        <div className="additional-reports-upload">

            <div className="additional-reports-upload-form">

            <div className="additional-reports-field">
                <label htmlFor="additional-report-upload-select">
                Select Report
                </label>

                <select
                id="additional-report-upload-select"
                value={uploadReportId}
                onChange={handleUploadReportChange}
                >
                <option value="">
                    Select a report
                </option>

                {reports.map((report) => (
                    <option
                    key={report.id}
                    value={report.id}
                    >
                    {report.report_name}
                    </option>
                ))}
                </select>
            </div>

            {uploadReportId && (
                <>
                <div className="additional-reports-field">
                    <label htmlFor="additional-report-upload-file">
                    XLSX File
                    </label>

                    <input
                    id="additional-report-upload-file"
                    type="file"
                    accept=".xlsx"
                    onChange={(event) => {
                        setUploadFile(
                        event.target.files?.[0] || null
                        );
                    }}
                    />
                </div>

                <div className="additional-reports-upload-actions">
                    <button
                    type="button"
                    className="additional-reports-primary-button"
                    onClick={handleUploadReport}
                    disabled={uploadingReport || !uploadFile}
                    >
                    {uploadingReport
                        ? "Uploading..."
                        : "Upload & Replace"}
                    </button>
                </div>
                </>
            )}
            </div>

            {uploadReportId && (
            <div className="additional-reports-upload-data">

                <div className="additional-reports-upload-data-header">
                <div>
                    <h3>Existing Data</h3>

                    <span>
                    {uploadTotalCount.toLocaleString()} rows
                    </span>
                </div>
                </div>

                {renderUploadReportTable()}

                {renderUploadPagination()}

            </div>
            )}

        </div>
        )}



      {/* =====================================================
          Details Modal
      ====================================================== */}

      {detailsReport ? (
        <div
          className="additional-reports-modal-backdrop"
          onClick={closeDetails}
        >
          <div
            className="additional-reports-modal"
            onClick={(event) =>
              event.stopPropagation()
            }
          >

            <div className="additional-reports-modal-header">
              <div>
                <h2>
                  {detailsReport.report_name}
                </h2>

                <p>
                  Report Configuration
                </p>
              </div>

              <button
                type="button"
                onClick={closeDetails}
                className="additional-reports-modal-close"
              >
                ×
              </button>
            </div>

            <div className="additional-reports-modal-body">

              <div className="additional-reports-detail-item">
                <strong>
                  Primary Report
                </strong>

                <span>
                  {detailsReport.connecting_report}
                </span>
              </div>

              <div className="additional-reports-detail-item">
                <strong>
                  Headers
                </strong>

                <div className="additional-reports-header-list">
                  {(
                    detailsReport.headers || []
                  ).map(
                    (header, index) => (
                      <span
                        key={`${header}-${index}`}
                        className="additional-reports-header-chip"
                      >
                        {header}
                      </span>
                    )
                  )}
                </div>
              </div>

              <div className="additional-reports-detail-item">

                <strong>
                  Identifier Mappings
                </strong>

                <div className="additional-reports-detail-mappings">

                  {Object.entries(
                    detailsReport.identifiers || {}
                  ).map(
                    (
                      [identifier, secondaryHeader]
                    ) => (
                      <div
                        className="additional-reports-detail-mapping"
                        key={identifier}
                      >

                        <span>
                          {identifier}
                        </span>

                        <span>
                          {secondaryHeader}
                        </span>

                        <span>
                          →
                        </span>

                        <span>
                          {
                            (
                              detailsReport
                                .connecting_columns ||
                              {}
                            )[identifier]
                          }
                        </span>

                      </div>
                    )
                  )}

                </div>

              </div>

            </div>

            <div className="additional-reports-modal-footer">

              <button
                type="button"
                className="additional-reports-secondary-button"
                onClick={closeDetails}
              >
                Close
              </button>

            </div>

          </div>
        </div>
      ) : null}

    </section>
  );
}

